/**
 * AES-GCM encryption and decryption using Web Crypto API
 * Zero-knowledge encryption: server never sees plaintext or keys
 */

import {
  generateRandomBytes,
  uint8ArrayToBase64,
  uint8ArrayToString,
  base64ToUint8Array,
  stringToUint8Array,
} from './utils';

export interface EncryptedData {
  c: string; // ciphertext (base64 encoded, includes auth tag)
  iv: string; // initialization vector (base64 encoded)
}

/**
 * Generate a random AES-256 encryption key
 * Used for encrypting secrets
 */
export async function generateEncryptionKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true, // extractable: we need to export the key for URL fragment
    ['encrypt', 'decrypt']
  );
}

/**
 * Export a CryptoKey to raw bytes (for URL fragment transmission)
 */
export async function exportKeyToBytes(key: CryptoKey): Promise<Uint8Array> {
  const exported = await crypto.subtle.exportKey('raw', key);
  return new Uint8Array(exported);
}

/**
 * Import raw bytes back to a CryptoKey
 */
export async function importKeyFromBytes(keyBytes: Uint8Array): Promise<CryptoKey> {
  // Create a copy to ensure we have a clean ArrayBuffer (not SharedArrayBuffer)
  const keyBytesBuffer = new ArrayBuffer(keyBytes.byteLength);
  new Uint8Array(keyBytesBuffer).set(keyBytes);

  return crypto.subtle.importKey('raw', keyBytesBuffer, { name: 'AES-GCM' }, true, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Encrypt plaintext with AES-256-GCM
 * Returns: { ciphertext, iv } in base64 format for storage
 *
 * @param plaintext - The secret to encrypt
 * @param key - CryptoKey for encryption
 * @returns Encrypted data with IV (needed for decryption)
 */
export async function encrypt(plaintext: string, key: CryptoKey): Promise<EncryptedData> {
  const iv = generateRandomBytes(12); // 96-bit IV for GCM
  const data = stringToUint8Array(plaintext);

  // Create clean ArrayBuffers (not SharedArrayBuffer)
  const ivBuffer = new ArrayBuffer(iv.byteLength);
  new Uint8Array(ivBuffer).set(iv);
  const dataBuffer = new ArrayBuffer(data.byteLength);
  new Uint8Array(dataBuffer).set(data);

  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: ivBuffer,
    },
    key,
    dataBuffer
  );

  // Web Crypto API includes the authentication tag in the ciphertext for GCM
  return {
    c: uint8ArrayToBase64(new Uint8Array(ciphertext)),
    iv: uint8ArrayToBase64(iv),
  };
}

/**
 * Decrypt AES-256-GCM ciphertext
 *
 * @param encryptedData - Object with base64-encoded ciphertext and IV
 * @param key - CryptoKey for decryption
 * @returns Decrypted plaintext
 * @throws If decryption fails (invalid key, corrupted data, wrong IV)
 */
export async function decrypt(
  encryptedData: EncryptedData,
  key: CryptoKey
): Promise<string> {
  const iv = base64ToUint8Array(encryptedData.iv);
  const ciphertext = base64ToUint8Array(encryptedData.c);

  // Create clean ArrayBuffers (not SharedArrayBuffer)
  const ivBuffer = new ArrayBuffer(iv.byteLength);
  new Uint8Array(ivBuffer).set(iv);
  const ciphertextBuffer = new ArrayBuffer(ciphertext.byteLength);
  new Uint8Array(ciphertextBuffer).set(ciphertext);

  try {
    const plaintext = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
      },
      key,
      ciphertextBuffer
    );

    return uint8ArrayToString(new Uint8Array(plaintext));
  } catch {
    throw new Error('Decryption failed: Invalid key, corrupted data, or wrong IV');
  }
}

/**
 * Encrypt with key bytes (convenience function)
 * Used when key is stored as base64 in URL fragment
 */
export async function encryptWithKeyBytes(
  plaintext: string,
  keyBytes: Uint8Array
): Promise<EncryptedData> {
  const key = await importKeyFromBytes(keyBytes);
  return encrypt(plaintext, key);
}

/**
 * Decrypt with key bytes (convenience function)
 * Used when key is retrieved from URL fragment
 */
export async function decryptWithKeyBytes(
  encryptedData: EncryptedData,
  keyBytes: Uint8Array
): Promise<string> {
  const key = await importKeyFromBytes(keyBytes);
  return decrypt(encryptedData, key);
}
